import React, { Component } from 'react';
import PropTypes from 'prop-types';
import compose from 'compose-function';

import withData from 'components/withData';
import LoadingIndicator from 'components/LoadingIndicator';
import { CountriesSection } from '../CountriesSection';
import { CompanyInfoSection } from '../CompanyInfoSection';
import { parse, getCodeFromItem, getItemByCode } from 'components/TreeSelector';

import { Form, FormHeader, FormTitle, FormDescription } from '../styles';

const metadataKeysPluralMap = {
	employeesNumber: 'employeesNumberBands',
	geographies: 'geographies',
	headOfficeLocation: 'headOfficeLocations',
	industry: 'industries',
	revenue: 'revenues',
};

class ExportMetadataFormBase extends Component {
	static propTypes = {
		onChange: PropTypes.func.isRequired,
		clearStaleState: PropTypes.func.isRequired,
	};

	state = {
		updatedGeographies: null,
		exportedMetadata: this.props.exportedMetadata,
	};

	componentWillUnmount() {
		this.props.clearStaleState();
	}

	componentDidUpdate({ response: prevResponse }) {
		const { response } = this.props;
		const { exportedMetadata } = this.state;

		if (!prevResponse.success && response.success) {
			const metadataOptions = response.success && response.data;
			const versionCleanedExportedMetadata = this.getVersionCleanedExportedMetadata(
				exportedMetadata,
				metadataOptions
			);

			if (versionCleanedExportedMetadata !== exportedMetadata) {
				this.setState({
					exportedMetadata: versionCleanedExportedMetadata,
				});
			}
		}
	}

	handleChange = data => {
		const { exportedMetadata } = this.state;

		this.setState(
			{
				exportedMetadata: { ...exportedMetadata, ...data },
			},
			this.triggerOnChange
		);
	};

	handleChangeGeo = ({ target: { value } }) => {
		this.setState(
			{
				updatedGeographies: value,
			},
			this.triggerOnChange
		);
	};

	triggerOnChange = () => {
		const {
			response: { data },
		} = this.props;
		const {
			updatedGeographies,
			exportedMetadata: {
				geographies: geographiesState,
				...exportedMetadataState
			},
		} = this.state;

		const geographies =
			updatedGeographies || this.getSelectedGeographies(geographiesState);

		const processedExportedMetadata = {};

		Object.entries(exportedMetadataState)
			.filter(([_0, value]) => value && value.data)
			.forEach(([key, value]) => {
				processedExportedMetadata[key] = {
					...value,
					data: { key: value.data.key },
				};
			});

		const exportedMetadata = {
			...processedExportedMetadata,
			geographies: {
				data:
					geographies.map(item =>
						getItemByCode(data.geographies.data || [], item)
					) || [],
				version: data.geographies.version,
			},
		};

		this.props.onChange({
			isFormValid: this.validateExportedMetadata(exportedMetadata),
			exportedMetadata,
		});
	};

	validateExportedMetadata = exportedMetadata => {
		const { geographies, ...data } = exportedMetadata;

		const isValid =
			geographies.data &&
			geographies.data.length > 0 &&
			Object.entries(data).length === 4 &&
			!!Object.entries(data).every(
				([_0, value]) => value.data && value.data.key
			);

		return isValid;
	};

	getVersionCleanedExportedMetadata = (exportedMetadata, metadataOptions) => {
		let cleanedExportedMetadata = {};

		Object.entries(exportedMetadata).forEach(([key, value]) => {
			const option = metadataOptions[metadataKeysPluralMap[key]];

			if (option && value && option.version !== value.version) {
				cleanedExportedMetadata[key] = {
					version: option.version,
				};
			}
		});

		return Object.keys(cleanedExportedMetadata).length > 0
			? { ...exportedMetadata, ...cleanedExportedMetadata }
			: exportedMetadata;
	};

	getParsedGeographies = metadataOptions =>
		parse(metadataOptions.geographies ? metadataOptions.geographies.data : []);

	getSelectedGeographies = geographies =>
		geographies && geographies.data
			? geographies.data.map(item => {
					return getCodeFromItem(item).id;
			  })
			: [];

	render() {
		const { exportedMetadata, updatedGeographies } = this.state;
		const { response } = this.props;

		if (response.loading) {
			return <LoadingIndicator loading />;
		}

		const metadataOptions = response.success && response.data;
		this.parsedGeographies =
			this.parsedGeographies || this.getParsedGeographies(metadataOptions);
		const selectedGeographies =
			updatedGeographies ||
			this.getSelectedGeographies(exportedMetadata.geographies);

		return (
			<Form>
				<FormHeader>
					<FormTitle>Knowledge Base Metadata</FormTitle>
					<FormDescription>
						<p>
							The below fields will be appended to any data you choose to export
							to the Knowledge Base for permanent storage and analysis by
							AlixPartners.{' '}
						</p>
						<p>
							Record the details for the business unit your engagement is with,
							not the parent organisation. You must complete all fields.
						</p>
					</FormDescription>
				</FormHeader>
				<CompanyInfoSection
					animationIndex={1}
					metadataOptions={metadataOptions}
					exportedMetadata={exportedMetadata}
					onChange={this.handleChange}
				/>
				<CountriesSection
					animationIndex={2}
					data={this.parsedGeographies}
					value={selectedGeographies}
					onChange={this.handleChangeGeo}
				/>
			</Form>
		);
	}
}

ExportMetadataFormBase.propTypes = {
	exportedMetadata: PropTypes.object.isRequired,
};

export const ExportMetadataForm = compose(withData('/companyMetadata/options'))(
	ExportMetadataFormBase
);
